/*
 * Firebase initialisation (SPEC §11). Config comes from REACT_APP_FB_* env vars
 * (frontend/.env.local — see .env.local.example). The web API key is not secret
 * (it ships to the browser); real protection is Security Rules (§13) + App Check.
 *
 * If env vars are absent, `isConfigured` is false and db/auth/storage are null —
 * the public site then renders entirely from the bundled seed (SPEC §10).
 */
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';

export const config = {
  apiKey: process.env.REACT_APP_FB_API_KEY,
  authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FB_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FB_APP_ID,
  measurementId: process.env.REACT_APP_FB_MEASUREMENT_ID,
};

export const isConfigured = Boolean(config.apiKey && config.projectId);

let app = null;
let db = null;
let auth = null;
let storage = null;

if (isConfigured) {
  app = initializeApp(config);
  db = getFirestore(app);
  auth = getAuth(app);
  storage = getStorage(app);

  // App Check (abuse protection for the open contact-form write + API). Enabled
  // when a reCAPTCHA v3 site key is provided. In dev, set
  // REACT_APP_APPCHECK_DEBUG=true and register the printed debug token in console.
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (siteKey) {
    // Lazy import so the App Check SDK only loads when actually used.
    import('firebase/app-check')
      .then(({ initializeAppCheck, ReCaptchaV3Provider }) => {
        if (process.env.REACT_APP_APPCHECK_DEBUG === 'true') {
          // eslint-disable-next-line no-self-assign
          window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }
        initializeAppCheck(app, {
          provider: new ReCaptchaV3Provider(siteKey),
          isTokenAutoRefreshEnabled: true,
        });
      })
      .catch(() => {});
  }

  // Analytics (optional) — only if a measurementId is present and supported.
  if (config.measurementId) {
    import('firebase/analytics')
      .then(({ getAnalytics, isSupported }) => isSupported().then((ok) => ok && getAnalytics(app)))
      .catch(() => {});
  }
} else if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.info('[firebase] not configured — using bundled seed content. Add frontend/.env.local to enable.');
}

export { app, db, auth, storage };
